import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/utils/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserCog, Save } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { MfaSettings } from "@/components/profile/MfaSettings";
import { isStaffOrAbove } from "@/utils/roles";

export default function ProfilePage() {
  useDocumentTitle("My profile", "Update your name and email");
  const { user, refresh } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
    }
  }, [user]);

  const dirty = user && (name.trim() !== (user.name || "") || email.trim().toLowerCase() !== (user.email || "").toLowerCase());

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!dirty) return;
    setSaving(true);
    try {
      const payload = {};
      if (name.trim() !== (user.name || "")) payload.name = name.trim();
      if (email.trim().toLowerCase() !== (user.email || "").toLowerCase()) payload.email = email.trim();
      await api.patch("/auth/me", payload);
      toast.success("Profile updated");
      if (refresh) await refresh();
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto space-y-4" data-testid="profile-page">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight flex items-center gap-2">
          <UserCog size={20} /> My profile
        </h1>
        <p className="text-sm text-muted-foreground">Update the name and email on your account.</p>
      </div>

      <div className="bg-white border border-border rounded-md p-5">
        {error && (
          <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="profile-error">
            {error}
          </div>
        )}
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-medium">Full name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="mt-1"
              data-testid="profile-name-input"
            />
          </div>
          <div>
            <Label className="text-xs font-medium">Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1"
              data-testid="profile-email-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              You'll use this email to sign in from now on.
            </p>
          </div>

          <div className="flex items-center gap-2 justify-end pt-2">
            <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="profile-cancel-btn">
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-slate-900 hover:bg-slate-800 text-white gap-1"
              disabled={saving || !dirty}
              data-testid="profile-save-btn"
            >
              <Save size={14} />
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </form>
      </div>
      {isStaffOrAbove(user?.role) && <MfaSettings />}
    </div>
  );
}
